/**
 * Keyboard shortcuts hook для master-detail UI отчёта расхождений
 * (фича 024 ③, Screen 3).
 *
 * Поведение (см. specs/024-discrepancy-card-redesign/README.md, секции
 * «Master-detail» и «Interactions & Behavior»):
 *
 * - `/`            → onSearchFocus() — работает всегда (включая feedOnly).
 * - `J` / `↓`      → next по orderedIds (без wrap). Только master-detail.
 * - `K` / `↑`      → prev по orderedIds (без wrap). Только master-detail.
 * - `Esc`          → onEscape?.(). Только master-detail.
 *
 * Все шорткаты пропускаются, если фокус в input/textarea/contenteditable,
 * либо `disabled === true`, либо `event.defaultPrevented`.
 *
 * Для буквенных шорткатов и стрелок игнорируем модификаторы Cmd/Ctrl/Alt/Shift.
 * Для `/` модификаторы НЕ блокируют срабатывание (на не-английских раскладках
 * `/` часто залетает с Shift).
 */

import { useEffect, useRef } from "react";

export interface UseDiscrepancyKbdOptions {
	/** ID текущего активного расхождения (для J/K/↑/↓ навигации). */
	activeId: string | null;
	/** Список ID в порядке, котором они показаны в master-pane. */
	orderedIds: readonly string[];
	/** Callback при выборе нового активного через клавиатуру. */
	onActiveChange: (id: string) => void;
	/** Callback для фокуса на search input (триггер `/`). */
	onSearchFocus?: () => void;
	/** Callback при Esc — родитель решает что делать (например, blur drawer). */
	onEscape?: () => void;
	/** True — слушать только `/` (feed-режим). False/undef — все шорткаты (master-detail). */
	feedOnly?: boolean;
	/** True — выключить хук (например, во время input-фокуса). */
	disabled?: boolean;
}

/**
 * Возвращает true, если событие пришло из элемента, в котором активно
 * редактирование текста (input/textarea/contenteditable). В таких случаях
 * перехватывать клавиатурные шорткаты нельзя.
 */
function isEditableTarget(target: EventTarget | null): boolean {
	if (!(target instanceof HTMLElement)) return false;
	const tag = target.tagName;
	if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
	if (target.isContentEditable) return true;
	return false;
}

export function useDiscrepancyKbd(opts: UseDiscrepancyKbdOptions): void {
	// Ref для всех опций — чтобы handler оставался стабильным между ререндерами,
	// а listener не пере-регистрировался при каждом изменении activeId/orderedIds.
	// Чистый `useRef + ref.current = opts` паттерн (без useEffectEvent —
	// для совместимости и потому что external libs не используем).
	const optsRef = useRef(opts);
	optsRef.current = opts;

	useEffect(() => {
		function handler(event: KeyboardEvent): void {
			const o = optsRef.current;

			if (o.disabled) return;
			if (event.defaultPrevented) return;
			if (isEditableTarget(event.target)) return;

			const key = event.key;

			// `/` — глобальный шорткат фокуса search. Модификаторы (Shift) допустимы
			// (на не-английских раскладках `/` часто требует Shift).
			if (key === "/") {
				// Ctrl/Cmd/Alt — пропускаем, чтобы не перехватывать системные комбо
				// типа Cmd+/ (комментарий в IDE-подобных контекстах).
				if (event.ctrlKey || event.metaKey || event.altKey) return;
				if (o.onSearchFocus) {
					event.preventDefault();
					o.onSearchFocus();
				}
				return;
			}

			// Все остальные шорткаты — только в master-detail режиме.
			if (o.feedOnly) return;

			// Для J/K/Arrow* — никаких модификаторов.
			if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
				return;
			}

			if (key === "Escape") {
				event.preventDefault();
				o.onEscape?.();
				return;
			}

			const isNext = key === "j" || key === "J" || key === "ArrowDown";
			const isPrev = key === "k" || key === "K" || key === "ArrowUp";

			if (!isNext && !isPrev) return;

			const ids = o.orderedIds;
			if (ids.length === 0) return;

			event.preventDefault();

			const currentIdx = o.activeId !== null ? ids.indexOf(o.activeId) : -1;

			if (isNext) {
				// Если activeId не найден или null — берём первый.
				if (currentIdx === -1) {
					o.onActiveChange(ids[0] as string);
					return;
				}
				// Стоп на последнем (без wrap).
				if (currentIdx >= ids.length - 1) return;
				o.onActiveChange(ids[currentIdx + 1] as string);
				return;
			}

			// isPrev
			if (currentIdx === -1) {
				o.onActiveChange(ids[0] as string);
				return;
			}
			// Стоп на первом (без wrap).
			if (currentIdx <= 0) return;
			o.onActiveChange(ids[currentIdx - 1] as string);
		}

		window.addEventListener("keydown", handler);
		return () => {
			window.removeEventListener("keydown", handler);
		};
		// Listener регистрируется один раз — все опции читаются через optsRef.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
}
